import { NotificationType, OrderStatus, Role, StructureStatus } from "@enums";
import {
  ArrowDownward,
  ArrowUpward,
  Cancel,
  CheckCircle,
  Comment,
  HourglassTop,
  Sync,
  Warning,
} from "@mui/icons-material";
import {
  Badge,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { toLocaleDateTimeString, useCheckUserRole } from "@utils";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { Timer } from "@pages/OrderTracker/components/PlanningAutomation/Timer";
import dayjs from "dayjs";

import {
  AutomationStatus,
  getStatusColour,
} from "@pages/OrderTracker/components/PlanningAutomation/utils";
import { OrderSearchOut } from "@providers/hop-ord-server/api";

import { TextOverflowTooltip } from "@components";
import { CtRtMockComponent } from "./CtRtMockComponent";
import type { SxProps, Theme } from "@mui/material/styles";

// Interface for the command centre tables
export interface CommandCenterTableProps {
  data: OrderSearchOut[] | undefined;
  isLoading: boolean;
  sortBy: string;
  sortDirection: string;
  handleSortClick: (sortBy: string) => void;
  noResultsMessage: string;
  toolTips: string[];
  showMockControls: boolean;
  isRo: boolean;
}

const getStructureBackgroundColor = (
  isDraft: boolean,
  status: string | null,
) => {
  if (isDraft) {
    return "grey.100";
  }

  switch (status) {
    case StructureStatus.PENDING:
      return "warning.light";
    case StructureStatus.COMPLETE:
    case StructureStatus.SKIPPED:
      return "success.light";
    default:
      return null;
  }
};

const getStructureStatusText = (isDraft: boolean, status: string | null) => {
  if (isDraft) {
    return "Not started";
  }

  switch (status) {
    case StructureStatus.COMPLETE:
      return "Available";
    case StructureStatus.SKIPPED:
      return "Skipped";
    case StructureStatus.PENDING:
    default:
      return "Pending";
  }
};

export const structStatusIcon = (status: string | null) => {
  switch (status) {
    case StructureStatus.COMPLETE:
      return <CheckCircle color="success" fontSize="large" />;
    case StructureStatus.SKIPPED:
      return <></>;

    default:
      return <Warning color="warning" fontSize="large" />;
  }
};

export const getPlanReviewStatusIcon = (orderStatus: string | null) => {
  switch (orderStatus) {
    case "ready_for_ro_approval":
      return <Warning color="warning" fontSize="large" />;
    case "approved":
      return <CheckCircle color="success" fontSize="large" />;
    case "rejected":
      return <Cancel color="error" fontSize="large" />;
    default:
      return <></>;
  }
};

export const searchTextToolTips = [
  "Check if you have any filters applied.",
  "Verify your spelling and try again.",
  "Just received a new careplan? Please wait one minute and refresh the page. ",
  "Try using the patient's first name and last name or patient ID.",
];

export const noSearchTextToolTips = [
  "Adjusting or clearing some filters.",
  "Checking if there are any active search terms.",
];

export const StatusIcon = ({
  status,
  fontSize = "inherit",
}: {
  status: AutomationStatus;
  fontSize?: string;
}): JSX.Element => {
  if (status === "success") {
    return <CheckCircle color="success" sx={{ fontSize }} />;
  }

  if (status === "not_started") {
    return <HourglassTop color="disabled" sx={{ fontSize }} />;
  }

  if (status === "in_progress") {
    return <Sync color="secondary" sx={{ fontSize }} />;
  }

  if (status === "error") {
    return <Cancel color="error" sx={{ fontSize }} />;
  }

  return <></>;
};

export const AutomationInProgress = (props: {
  order: OrderSearchOut;
  navToTracker: (orderId: number, orderStatus: string) => void;
}) => {
  const planGenerationAttempt = props.order.latestPlanGenerationAttempt;
  const status = planGenerationAttempt?.status;
  const warnings = planGenerationAttempt?.warnings?.length || 0;
  const planProgressPercentage = planGenerationAttempt?.planProgressPercentage;
  const completedTime = planGenerationAttempt?.result?.finishedAt;
  const planName = planGenerationAttempt?.relatedPrescription;

  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        justifyContent="space-between"
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <StatusIcon status={status as AutomationStatus} />
          <Typography
            variant="body2"
            alignItems="center"
            noWrap
            textOverflow="ellipsis"
            color={status === "error" ? "error" : "text.primary"}
            sx={{ maxWidth: "390px" }}
          >
            {planName || props.order.orderName}
          </Typography>

          {warnings > 0 && <Warning color="warning" fontSize="small" />}
        </Stack>
        <Button
          variant="outlined"
          onClick={() => props.navToTracker(props.order.id, props.order.status)}
        >
          <Typography noWrap variant="subtitle2">
            Auto planning
          </Typography>
        </Button>
      </Stack>

      <LinearProgress
        sx={{ marginBottom: 1, marginTop: 1 }}
        variant="determinate"
        value={planProgressPercentage}
        color={getStatusColour(status as AutomationStatus)}
      />
      <Stack direction="row">
        <Typography variant="body2" color="text.secondary"></Typography>
        {status === "not_started" && (
          <Typography variant="body2" color="disabled">
            Queued
          </Typography>
        )}
        {status === "error" && (
          <Typography variant="body2" color="error">
            Automated Plan | Failed{" "}
            {toLocaleDateTimeString(completedTime || "")}
          </Typography>
        )}
        {status === "in_progress" && (
          <Typography variant="body2" color="text.secondary">
            Automated Plan | Processing:{" "}
            {(planGenerationAttempt?.currentStepIndex || 0) + 1}/
            {planGenerationAttempt?.stepSequence?.length}{" "}
            {planGenerationAttempt?.currentStep}
          </Typography>
        )}
        {status === "success" && (
          <>
            <Typography variant="body2" color="text.secondary">
              Automated Plan | Elapsed time:
            </Typography>{" "}
            &nbsp;
            <Timer
              startTime={
                dayjs(planGenerationAttempt?.planningStartedAt) || null
              }
              completedTime={dayjs(completedTime)}
            />
          </>
        )}
      </Stack>
    </Stack>
  );
};

export const PlanningStatusCell = (props: { order: OrderSearchOut }) => {
  const navigate = useNavigate();
  const isRo = useCheckUserRole(Role.RO);
  const navToTracker = useCallback(
    (orderId: number, orderStatus: string) => {
      if (orderStatus === OrderStatus.READY_FOR_RO_APPROVAL && isRo) {
        return navigate(`/review/${orderId}`);
      }
      let urlEnd = "";
      if (orderStatus === OrderStatus.READY_FOR_RO_APPROVAL) {
        urlEnd = "plan-review";
      } else if (orderStatus === OrderStatus.SENT_TO_AUTOMATION) {
        urlEnd = "automated";
      }
      return navigate(`/commandcenter/${orderId}/${urlEnd}`);
    },
    [navigate, isRo],
  );

  const order = props.order;
  const status = order.status;
  const orderId = order.id;

  if (status === OrderStatus.SENT_TO_AUTOMATION) {
    return <AutomationInProgress order={order} navToTracker={navToTracker} />;
  }

  if (status === OrderStatus.COMPLETED) {
    return (
      <Button
        variant="outlined"
        onClick={() => navigate(`/commandcenter/${orderId}/handover`)}
      >
        <Typography noWrap variant="subtitle2">
          Auto planning
        </Typography>
      </Button>
    );
  }

  // If draft navigate to the create careplan page
  if (status === OrderStatus.DRAFT) {
    return (
      <Button variant="outlined" onClick={() => navigate(`/order/${orderId}`)}>
        <Typography noWrap variant="subtitle2">
          Continue editing
        </Typography>
      </Button>
    );
  }

  // Otherwise use nav tracker logic and show auto planning button
  return (
    <Button variant="outlined" onClick={() => navToTracker(orderId, status)}>
      <Typography noWrap variant="subtitle2">
        Auto planning
      </Typography>
    </Button>
  );
};

export const CommentsIconButton = ({
  orderId,
  orderStatus,
  unreadCommentCount,
}: {
  orderId: number;
  orderStatus?: string;
  unreadCommentCount: number;
}) => {
  const navigate = useNavigate();
  const isRo = useCheckUserRole(Role.RO);

  const handleCommentClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();

      if (!orderId) {
        return;
      }

      let path = `/order/${orderId}`;

      // This reuses the same navigation logic as notification.
      if (orderStatus && orderStatus !== OrderStatus.DRAFT) {
        if (isRo) {
          path = `/review/${orderId}`;
        } else {
          path = `/commandcenter/${orderId}/plan-review`;
        }
      }

      navigate(path, {
        state: {
          notificationType: NotificationType.NEW_COMMENT,
        },
      });
    },
    [navigate, isRo, orderId, orderStatus],
  );

  return (
    <IconButton
      size="small"
      sx={{
        paddingRight: 2,
      }}
      data-testid={"comments-button"}
      onClick={handleCommentClick}
    >
      <Badge badgeContent={unreadCommentCount} color="error">
        <Comment fontSize="small" />
      </Badge>
    </IconButton>
  );
};

export const ViewPlanButton = ({ orderId }: { orderId: number }) => {
  const navigate = useNavigate();
  const navToTracker = useCallback(
    (orderId: number) => {
      return navigate(`/review/${orderId}`);
    },
    [navigate],
  );

  return (
    <Button variant="contained" onClick={() => navToTracker(orderId)}>
      <Typography noWrap variant="subtitle2">
        View plan
      </Typography>
    </Button>
  );
};

const TestCaseChip = ({ small }: { small?: boolean }) => {
  return (
    <Chip
      color="info"
      sx={{ height: small ? 25 : "default" }}
      label={"Test Case"}
      variant="outlined"
    />
  );
};

const PatientCell = ({
  orderId,
  orderStatus,
  patientName,
  patientMrn,
  patientDob,
  treatingDepartment,
  treatingRo,
  unreadCommentCount,
  isTest,
  stickyColumn,
}: {
  orderId: number;
  orderStatus: string;
  patientName: string;
  patientMrn: string;
  patientDob: string;
  treatingDepartment: string;
  treatingRo: string;
  unreadCommentCount: number;
  isTest: boolean;
  stickyColumn: SxProps<Theme>;
}) => (
  <TableCell scope="row" sx={{ ...stickyColumn }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Stack direction="column" flex={1}>
        <Stack direction="row" gap={1} alignItems="center">
          <Typography variant="subtitle2">{patientName}</Typography>
          {isTest && <TestCaseChip small />}
        </Stack>
        <Typography variant="body2">
          Pat ID: {patientMrn} | DOB:{" "}
          {patientDob ? toLocaleDateTimeString(patientDob, "date") : "-"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Clinic: {treatingDepartment} | Physician: {treatingRo}
        </Typography>
      </Stack>
      <CommentsIconButton
        orderId={orderId}
        orderStatus={orderStatus}
        unreadCommentCount={unreadCommentCount || 0}
      />
    </Stack>
  </TableCell>
);

const SubmittedCell = ({
  orderStatus,
  submittedDatetime,
}: {
  orderStatus: string;
  submittedDatetime: string;
}) => {
  const isDraft = orderStatus === "draft";
  return (
    <TableCell
      sx={{
        backgroundColor: isDraft ? "grey.100" : "success.light",
      }}
    >
      <Typography variant="body2">
        {submittedDatetime
          ? toLocaleDateTimeString(submittedDatetime)
          : isDraft
            ? "Draft"
            : "-"}
      </Typography>
    </TableCell>
  );
};

const CareplanCell = ({
  orderStatus,
  orderName,
  planDelivery,
  index,
}: {
  orderStatus: string;
  orderName: string;
  planDelivery: string;
  index: number;
}) => {
  const isDraft = orderStatus === "draft";
  return (
    <TableCell
      sx={{
        backgroundColor: isDraft ? "grey.100" : "success.light",
      }}
    >
      <Stack gap={0.5}>
        <TextOverflowTooltip
          variant="body2"
          data-testid={`${index}-prescription-name`}
          forceTooltip={true}
          noWrap={false}
          sx={{
            display: "-webkit-box",
            overflow: "hidden",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
          }}
        >
          {orderName}
        </TextOverflowTooltip>
        <Typography
          variant="body2"
          color={
            planDelivery === "emergency"
              ? "error.dark"
              : planDelivery === "urgent"
                ? "warning.dark"
                : "text.primary"
          }
          sx={{ textTransform: "capitalize" }}
        >
          {planDelivery}
        </Typography>
      </Stack>
    </TableCell>
  );
};

const PlanReviewCell = ({
  order,
  index,
}: {
  order: OrderSearchOut;
  index: number;
}) => (
  <TableCell
    sx={{
      backgroundColor:
        order?.status === "ready_for_ro_approval"
          ? "warning.light"
          : order?.status === "approved"
            ? "success.light"
            : order?.status === "rejected"
              ? "error.light"
              : "grey.100",
    }}
    data-testid={`${index}-plan-review-status`}
  >
    <Stack alignItems="center" width={1} gap={1}>
      {getPlanReviewStatusIcon(order?.status)}
      <Typography
        variant="body2"
        color={
          ["approved", "rejected"].includes(order?.status)
            ? "text.primary"
            : "text.secondary"
        }
      >
        {order?.status === "ready_for_ro_approval"
          ? "Pending"
          : order?.status === "approved"
            ? "Approved"
            : order?.status === "rejected"
              ? "Rejected"
              : "Not started"}
      </Typography>
    </Stack>
  </TableCell>
);

const HandoverCell = ({
  order,
  index,
}: {
  order: OrderSearchOut;
  index: number;
}) => (
  <TableCell
    sx={{
      backgroundColor: order.handoverDatetime ? "success.light" : "grey.100",
    }}
    data-testid={`${index}-handover-status`}
  >
    <Stack alignItems="center" width={1} gap={1}>
      {order.handoverDatetime && (
        <CheckCircle color="success" fontSize="large" />
      )}
      <Typography
        color={order.handoverDatetime ? "text.primary" : "text.secondary"}
        variant="body2"
      >
        {order.handoverDatetime
          ? dayjs(order.handoverDatetime).format("MMM D")
          : "Not started"}
      </Typography>
    </Stack>
  </TableCell>
);

export const RoTableHeader = ({
  stickyColumn,
  sortBy,
  sortDirection,
  handleSortClick,
}: {
  stickyColumn: SxProps<Theme>;
  sortBy: string;
  sortDirection: string;
  handleSortClick: (sortBy: string) => void;
}) => {
  return (
    <TableHead>
      <TableRow>
        <TableCell width="22%" sx={{ ...stickyColumn, zIndex: "11" }}>
          Patient
        </TableCell>
        <TableCell width="10%">
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{ cursor: "pointer" }}
            onClick={() => handleSortClick("rxSubmitted")}
          >
            {sortBy === "rxSubmitted" && sortDirection === "asc" ? (
              <ArrowUpward color="secondary" fontSize="small" />
            ) : null}
            {sortBy === "rxSubmitted" && sortDirection === "desc" ? (
              <ArrowDownward color="secondary" fontSize="small" />
            ) : null}
            Rx Submitted
          </Stack>
        </TableCell>
        <TableCell width="24%">Careplan</TableCell>
        <TableCell width="7%" sx={{ textAlign: "left" }}>
          Plan Review
        </TableCell>
        <TableCell width="30%" sx={{ textAlign: "left" }}>
          Action
        </TableCell>
        <TableCell width="7%" sx={{ textAlign: "center" }}>
          Handover
        </TableCell>
      </TableRow>
    </TableHead>
  );
};

export const RoTableRow = ({
  order,
  index,
  stickyColumn,
}: {
  order: OrderSearchOut;
  index: number;
  stickyColumn: SxProps<Theme>;
}) => {
  const isDraft = order.status === "draft";

  return (
    <TableRow
      key={index}
      sx={{
        "&:last-child td, &:last-child th": { border: 0 },
        height: "102px",
        minHeight: "102px",
      }}
    >
      <PatientCell
        orderId={order.id}
        orderStatus={order.status}
        patientName={order.patientName ?? ""}
        patientMrn={order.patientMrn ?? ""}
        patientDob={order.patientDob ?? ""}
        treatingDepartment={order.treatingDepartment ?? ""}
        treatingRo={order.treatingRo}
        unreadCommentCount={order.unreadCommentCount}
        isTest={order.isTest}
        stickyColumn={stickyColumn}
      />
      <SubmittedCell
        orderStatus={order.status}
        submittedDatetime={order.submittedDatetime ?? ""}
      />
      <CareplanCell
        orderStatus={order.status}
        orderName={order.orderName ?? ""}
        planDelivery={order.planDelivery ?? ""}
        index={index}
      />
      <PlanReviewCell order={order} index={index} />
      <TableCell
        sx={{
          backgroundColor: isDraft ? "grey.100" : "success.light",
        }}
      >
        <ViewPlanButton orderId={order.id} />
      </TableCell>
      <HandoverCell order={order} index={index} />
    </TableRow>
  );
};

export const DosiTableHeader = ({
  stickyColumn,
  sortBy,
  sortDirection,
  handleSortClick,
}: {
  stickyColumn: SxProps<Theme>;
  sortBy: string;
  sortDirection: string;
  handleSortClick: (sortBy: string) => void;
}) => {
  return (
    <TableHead>
      <TableRow>
        <TableCell width="19%" sx={{ ...stickyColumn, zIndex: "11" }}>
          Patient
        </TableCell>
        <TableCell width="9%">
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{ cursor: "pointer" }}
            onClick={() => handleSortClick("rxSubmitted")}
          >
            {sortBy === "rxSubmitted" && sortDirection === "asc" ? (
              <ArrowUpward color="secondary" fontSize="small" />
            ) : null}
            {sortBy === "rxSubmitted" && sortDirection === "desc" ? (
              <ArrowDownward color="secondary" fontSize="small" />
            ) : null}
            Rx Submitted
          </Stack>
        </TableCell>
        <TableCell width="15%">Careplan</TableCell>
        <TableCell width="7%" sx={{ textAlign: "center" }}>
          CT
        </TableCell>
        <TableCell width="7%" sx={{ textAlign: "center" }}>
          RTStruct
        </TableCell>
        <TableCell width="29%">Planning Status</TableCell>
        <TableCell width="7%" sx={{ textAlign: "left" }}>
          Plan Review
        </TableCell>
        <TableCell width="7%" sx={{ textAlign: "center" }}>
          Handover
        </TableCell>
      </TableRow>
    </TableHead>
  );
};

export const DosiTableRow = ({
  order,
  index,
  stickyColumn,
  showMockControls,
}: {
  order: OrderSearchOut;
  index: number;
  stickyColumn: SxProps<Theme>;
  showMockControls: boolean;
}) => {
  const isDraft = order.status === "draft";

  return (
    <TableRow
      key={index}
      sx={{
        "&:last-child td, &:last-child th": { border: 0 },
        height: "102px",
        minHeight: "102px",
      }}
    >
      <PatientCell
        orderId={order.id}
        orderStatus={order.status}
        patientName={order.patientName ?? ""}
        patientMrn={order.patientMrn ?? ""}
        patientDob={order.patientDob ?? ""}
        treatingDepartment={order.treatingDepartment ?? ""}
        treatingRo={order.treatingRo}
        unreadCommentCount={order.unreadCommentCount}
        isTest={order.isTest}
        stickyColumn={stickyColumn}
      />
      <SubmittedCell
        orderStatus={order.status}
        submittedDatetime={order.submittedDatetime ?? ""}
      />
      <CareplanCell
        orderStatus={order.status}
        orderName={order.orderName ?? ""}
        planDelivery={order.planDelivery ?? ""}
        index={index}
      />

      {/* CT Cell */}
      <TableCell
        sx={{
          backgroundColor: getStructureBackgroundColor(isDraft, order.ctStatus),
        }}
      >
        <Stack alignItems="center" width={1} gap={1}>
          {!isDraft && structStatusIcon(order.ctStatus)}
          <Typography
            color={
              order?.ctStatus === StructureStatus.COMPLETE && !isDraft
                ? "text.primary"
                : "text.secondary"
            }
            variant="body2"
            data-testid={`${index}-ct-status-text`}
          >
            {getStructureStatusText(isDraft, order?.ctStatus)}
          </Typography>
          {order?.ctStatus === StructureStatus.PENDING && showMockControls && (
            <CtRtMockComponent orderId={order?.id} ctOnly={false} />
          )}
        </Stack>
      </TableCell>

      {/* RTStruct Cell */}
      <TableCell
        sx={{
          backgroundColor: getStructureBackgroundColor(
            isDraft,
            order.rtstructStatus,
          ),
        }}
      >
        <Stack alignItems="center" width={1} gap={1}>
          {!isDraft && structStatusIcon(order?.rtstructStatus)}
          <Typography
            color={
              order?.rtstructStatus === StructureStatus.COMPLETE && !isDraft
                ? "text.primary"
                : "text.secondary"
            }
            variant="body2"
            data-testid={`${index}-rtstruct-status-text`}
          >
            {getStructureStatusText(isDraft, order?.rtstructStatus)}
          </Typography>
          {order?.ctStatus === StructureStatus.PENDING && showMockControls && (
            <CtRtMockComponent orderId={order?.id} ctOnly={false} />
          )}
        </Stack>
      </TableCell>

      {/* Planning Status Cell */}
      <TableCell
        sx={{
          backgroundColor:
            order?.status === "manual_plan"
              ? "success.light"
              : order?.latestPlanGenerationAttempt?.status === "success"
                ? "success.light"
                : order?.latestPlanGenerationAttempt?.status === "warning"
                  ? "warning.light"
                  : order?.latestPlanGenerationAttempt?.status === "error"
                    ? "error.light"
                    : order?.latestPlanGenerationAttempt?.status ===
                        "in_progress"
                      ? "customBackground.main"
                      : "grey.100",
        }}
        data-testid={`${index}-planning-status`}
      >
        <PlanningStatusCell order={order} />
      </TableCell>

      <PlanReviewCell order={order} index={index} />

      <HandoverCell order={order} index={index} />
    </TableRow>
  );
};

export const NoResultsRow = ({
  colSpan,
  isLoading,
  noResultsMessage,
  toolTips,
}: {
  colSpan: number;
  isLoading: boolean;
  noResultsMessage: string;
  toolTips: string[];
}) => {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ border: "none !important" }}>
        {isLoading ? (
          <LinearProgress color="primary" />
        ) : (
          <Stack padding={2}>
            <Typography variant="subtitle2">
              {`No results found${noResultsMessage}.`}
            </Typography>
            <Typography paddingTop={2} variant="overline">
              TRY
            </Typography>
            {toolTips.map((tip, index) => (
              <Typography component="li" key={index} variant="body2">
                {tip}
              </Typography>
            ))}
          </Stack>
        )}
      </TableCell>
    </TableRow>
  );
};

export const CommandCenterTable = ({
  data,
  isLoading,
  sortBy,
  sortDirection,
  handleSortClick,
  noResultsMessage,
  toolTips,
  showMockControls,
  isRo,
}: CommandCenterTableProps) => {
  const stickyColumn: SxProps<Theme> = {
    position: "sticky",
    left: 0,
    zIndex: "10",
    backgroundColor: "background.paper",
  };
  return (
    <TableContainer
      sx={{
        flexGrow: 1,
        overflow: "auto",
        height: 1,
        maxHeight: "calc(100vh - 250px)",
      }}
    >
      <Table
        stickyHeader
        size="small"
        sx={{
          minWidth: "1200px",
          "& .MuiTableCell-head": { padding: 2 },
          "& .MuiTableCell-body": {
            padding: isLoading ? 0 : 2,
            borderRight: "1px solid",
            borderRightColor: "divider",
          },
          "& .MuiTableRow-root:last-child td": {
            borderRight: "1px solid",
            borderRightColor: "divider",
          },
          "& .MuiTableCell-root:last-child": {
            borderRight: 0,
          },
        }}
      >
        {isRo ? (
          <RoTableHeader
            stickyColumn={stickyColumn}
            sortBy={sortBy}
            sortDirection={sortDirection}
            handleSortClick={handleSortClick}
          />
        ) : (
          <DosiTableHeader
            stickyColumn={stickyColumn}
            sortBy={sortBy}
            sortDirection={sortDirection}
            handleSortClick={handleSortClick}
          />
        )}
        <TableBody>
          {!data?.length || isLoading ? (
            <NoResultsRow
              colSpan={isRo ? 6 : 8}
              isLoading={isLoading}
              noResultsMessage={noResultsMessage}
              toolTips={toolTips}
            />
          ) : (
            data?.map((order, index) => {
              return isRo ? (
                <RoTableRow
                  key={index}
                  order={order}
                  index={index}
                  stickyColumn={stickyColumn}
                />
              ) : (
                <DosiTableRow
                  key={index}
                  order={order}
                  index={index}
                  stickyColumn={stickyColumn}
                  showMockControls={showMockControls}
                />
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
